import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FormContext } from './Form';
import Heading from './Heading';
import Row from '../layout/Row';
import Button from './Button';
import { Editor, EditorState, Modifier, RichUtils } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { shortHandDate } from '../utility/functions';
import { stateFromHTML } from 'draft-js-import-html';
import ModalFactory from '../Modal';

const Wrapper = styled.div`
    display: flex;
    flex-direction: ${({ direction }) => direction};
    gap: 0.5em;
`;

const TextAreaCSS = styled.div`
    display: block;
    border: 1px solid ${({ value, theme }) => value ? theme.gray : `${theme.gray}40`};
    border-radius: 0.2rem;
    color: ${({ theme }) => theme.text};
    transition: background-color, transform, 0.3s;
    background-color: ${({ theme }) => theme.input.background};
    padding: 0.3em 0.5em;
    font-size: inherit;
    white-space: pre-line;
    width: auto;

    max-height: 30em;
    overflow: auto;

    ${({ disable, theme }) => disable ? `
        color: ${theme.input.disabledText};
        background-color: ${theme.input.disabled};
        transform: unset;
        box-shadow: unset;
        cursor: default;
    `: null}

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    &::placeholder {
        color: ${({ theme }) => theme.input.disabled}40;
    }
    &:focus {
        outline: none !important;
        border: 1px solid ${({ theme }) => theme.text};
        box-shadow: 0 0 3px ${({ theme }) => theme.highlight};
    }
`;

const ButtonRow = styled(Row)`
    flex-wrap: wrap;
`;

const Preview = styled.div`
    p {
        margin: 0;
    }
`;

const OptionalFlag = styled.i`
    color: ${({ theme }) => theme.gray};
    font-weight: 500;
    font-size: 0.8em;
`;

const TextArea = ({ context, ...props }) => {
    const editorRef = useRef(null);

    const decodeHTMLEntities = (text) => {
        const element = document.createElement("textarea");
        element.innerHTML = text;
        return element.value;
    }

    const EditorContent = () => {
        if (context ? context.values[props.name] : props.value) {
            let value = decodeHTMLEntities(context ? context.values[props.name] : props.value);
            const contentState = stateFromHTML(value);
            return EditorState.createWithContent(contentState);
        }
        return EditorState.createEmpty();
    }

    const [editorState, setEditorState] = useState(() => EditorContent());

    useEffect(() => {
        if (!context) return;
        context.setValues(v => ({
            ...v,
            [props.name]: stateToHTML(editorState.getCurrentContent())
        }))
    }, [editorState]);

    useEffect(() => {
        if (!context) return;
        if (context.values[props.name] !== stateToHTML(editorState.getCurrentContent())) {
            setEditorState(EditorContent());
        }
    }, [context?.editing]);


    useEffect(() => {
        if (!context) return;

        if (context.values[props.name] !== stateToHTML(editorState.getCurrentContent())) {
            setEditorState(EditorContent());
        }
    }, [context?.values])

    const applyInLine = (format) => {
        validateText(RichUtils.toggleInlineStyle(editorState, format));
    }

    // const applyBlockType = (format) => {
    //     validateText(RichUtils.toggleBlockType(editorState, format));
    // }

    const validateText = (e) => {
        // if (props.tags) {
        //     const selection = editorState.getSelection();
        //     const rawContentState = stateToHTML(e.getCurrentContent());
        //     let parsedHTML = rawContentState;
        //     const tagRegex = /\{(.*?)\}/g;
        //     let tagMatched = false;
        //     parsedHTML.match(tagRegex)?.forEach(match => {
        //         if (!props.tags.find(f => match === `{ ${f} }`)) {
        //             parsedHTML = parsedHTML.replace(match, '');
        //             tagMatched = true
        //         }
        //     });

        //     if (tagMatched) {
        //         const newContentState = stateFromHTML(parsedHTML)

        //         const newState = EditorState.createWithContent(newContentState);
        //         const contentStateLength = newContentState.getPlainText().length;
        //         const selectionState = newState.getSelection();
        //         const newSelectionState = selectionState.merge({
        //             anchorOffset: Math.min(selection.anchorOffset, contentStateLength),
        //             focusOffset: Math.min(selection.focusOffset, contentStateLength),
        //         });
        //         const updatedEditorState = EditorState.forceSelection(newState, newSelectionState);
        //         setEditorState(updatedEditorState);
        //     } else {
        //         setEditorState(e);
        //     }
        // } else {
        //     setEditorState(e);
        // }
        setEditorState(e);
    }

    const parseKeys = (chars) => {
        if (chars === '{' || chars === '}') {
            return 'handled';
        }
        return 'not-handled';
    }

    const handlePastedText = (text, html, editorState) => {
        const modifiedText = text.replace(/[{}]/g, '');
        const contentState = Modifier.replaceText(
            editorState.getCurrentContent(),
            editorState.getSelection(),
            modifiedText
        );
        const newEditorState = EditorState.push(
            editorState,
            contentState,
            'insert-characters'
        );
        setEditorState(newEditorState);
        return 'handled';
    };


    const addTag = (tag) => {
        const currentContent = editorState.getCurrentContent();
        const currentSelection = editorState.getSelection();

        let newContentState;
        if (currentSelection.anchorOffset !== currentSelection.focusOffset) {
            newContentState = Modifier.replaceText(
                currentContent,
                currentSelection,
                `{ ${tag} }`
            );
        } else {
            newContentState = Modifier.insertText(
                currentContent,
                currentSelection,
                `{ ${tag} }`
            );
        }

        const newEditorState = EditorState.push(
            editorState,
            newContentState,
            'insert-characters'
        );
        validateText(newEditorState);
    }

    const previewText = () => {
        const dummyTags = [
            {
                tag: '{ FirstName }',
                dummy: 'John'
            },
            {
                tag: '{ LastName }',
                dummy: 'Doe'
            },
            {
                tag: '{ FullName }',
                dummy: 'John Doe'
            },
            {
                tag: '{ PassProgram }',
                dummy: 'Basic Wash'
            },
            {
                tag: '{ PassProgram }',
                dummy: 'Basic Wash'
            },
            {
                tag: '{ UsesRemaining }',
                dummy: '3'
            },
            {
                tag: '{ UseCount }',
                dummy: '5'
            },
            {
                tag: '{ MaxUses }',
                dummy: '7'
            },
            {
                tag: '{ KioskItemName }',
                dummy: 'A Wash'
            },
            {
                tag: '{ ExpirationDate }',
                dummy: shortHandDate(new Date().toDateString())
            },
        ];

        let HTML = stateToHTML(editorState.getCurrentContent());
        dummyTags.forEach(e => HTML = HTML.replaceAll(e.tag, e.dummy));
        ModalFactory.show({
            title: 'Preview',
            body: <Preview dangerouslySetInnerHTML={{ __html: HTML }} />
        })
    }

    return props.standalone ? (
        <Wrapper direction={props.row === true ? 'row' : 'column'}>
            <Heading fontSize={0.8}>
                {props.label}
                {context && !props.required && <OptionalFlag>- Optional</OptionalFlag>}
            </Heading>

            <TextAreaCSS
                value={(context ? context.values[props.name] : props.value) || ''}
                disable={context ? context.submitting || context.editing || props.disable : props.disable}
            >
                <Editor
                    ref={editorRef}
                    handleBeforeInput={parseKeys}
                    handlePastedText={handlePastedText}
                    editorState={editorState}
                    onChange={validateText}
                    readOnly={context ? context.submitting || context.editing || props.disable : props.disable}
                />
            </TextAreaCSS>
            {context && !context.editing && !props.justTags ?
                <ButtonRow>
                    {/* <Button onClick={() => applyBlockType('header-three')}>Heading</Button> */}
                    <Button onClick={() => applyInLine('BOLD')}>Bold</Button>
                    <Button onClick={() => applyInLine('ITALIC')}>Italic</Button>
                    <Button onClick={() => applyInLine('UNDERLINE')}>Underline</Button>
                </ButtonRow>
                : null
            }
            {props.tags && (
                <ButtonRow>
                    {!props.justTags && <Button icon='magnifyingGlass' onClick={previewText}>Preview</Button>}
                    {!context.editing ? props.tags.map((e, i) => <Button key={`Tag-${i}`} onClick={() => addTag(e)}>{e}</Button>) : null}
                </ButtonRow>
            )}
        </Wrapper>
    ) : (
        <TextAreaContext
            standalone={1}
            {...props}
        />
    );
}

export default TextArea;

const TextAreaContext = ({ ...props }) => {
    const context = useContext(FormContext);
    return (
        <TextArea
            context={{ ...context }}
            {...props}
        />
    )
}
